import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, Globe, User, LogOut, LayoutDashboard, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';
import { BRAND } from '../brand';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Transparent only on landing top — solid elsewhere & on scroll
  const isLanding = location.pathname === '/';
  const transparent = isLanding && !scrolled && !mobileMenuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const linkBase = 'text-sm font-mont font-light tracking-wide transition-colors';
  const linkColor = transparent ? 'text-white/90 hover:text-lime' : 'text-ink-800 hover:text-ink';

  return (
    <nav
      className={[
        'sticky top-0 z-50 transition-all duration-300',
        transparent
          ? 'bg-transparent border-b border-transparent'
          : 'bg-white/90 backdrop-blur-md border-b border-black/5 shadow-[0_1px_0_rgba(0,0,0,0.02)]',
      ].join(' ')}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo-link">
            <img
              src={transparent ? BRAND.logos.wordmarkLight : BRAND.logos.wordmarkDark}
              alt="Mobility"
              className="h-9 sm:h-11 w-auto select-none"
              draggable={false}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-7">
            <Link to="/search" className={`${linkBase} ${linkColor}`}>
              {t('search')}
            </Link>
            <Link to="/#mundial-2026" className={`${linkBase} ${linkColor}`}>
              {t('navWorldCup')}
            </Link>
            <Link to="/how-it-works" className={`${linkBase} ${linkColor}`}>
              {t('howItWorks')}
            </Link>
            <Link to="/become-mobilitier" className={`${linkBase} ${linkColor}`}>
              {t('becomeMobilitier')}
            </Link>
          </div>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className={transparent ? 'text-white/90 hover:bg-white/10 hover:text-white' : 'text-ink-700'}
              data-testid="navbar-lang-toggle"
            >
              <Globe className="h-4 w-4 mr-1" />
              <span className="text-xs font-medium">{language.toUpperCase()}</span>
            </Button>

            {user ? (
              <>
                <Link
                  to="/messages"
                  className={transparent ? 'text-white/90 hover:text-lime' : 'text-ink-700 hover:text-ink'}
                  data-testid="nav-messages-link"
                  aria-label="Mensajes"
                >
                  <MessageCircle className="h-5 w-5" />
                </Link>
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`flex items-center gap-2 ${transparent ? 'text-white hover:bg-white/10' : 'text-ink-800'}`}
                      data-testid="user-menu-trigger"
                    >
                      <User className="h-4 w-4" />
                      <span className="text-sm font-medium">{user.name?.split(' ')[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 font-mont">
                    {/* Role-aware dashboard links */}
                    {user.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin/dashboard')} data-testid="menu-admin-panel">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Admin Panel
                      </DropdownMenuItem>
                    )}
                    {user.role !== 'admin' && (
                      <>
                        {/* Show Dashboard (Renter) if primary or secondary == user */}
                        {(user.role === 'user' || user.secondary_role === 'user') && (
                          <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                            <LayoutDashboard className="mr-2 h-4 w-4" />
                            Dashboard
                          </DropdownMenuItem>
                        )}
                        {/* Show Rental-Hub (Mobilitier) if primary or secondary == mobilitier */}
                        {(user.role === 'mobilitier' || user.secondary_role === 'mobilitier') && (
                          <DropdownMenuItem onClick={() => navigate('/mobilitier/dashboard')} data-testid="menu-rental-hub">
                            <LayoutDashboard className="mr-2 h-4 w-4" />
                            Rental-Hub
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                      <User className="mr-2 h-4 w-4" />
                      {t('myProfile')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="mr-2 h-4 w-4" />
                      {t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className={transparent ? 'text-white hover:bg-white/10' : 'text-ink-800 hover:bg-ink-50'}
                  data-testid="navbar-login-btn"
                >
                  {t('login')}
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="rounded-full px-5 h-10 font-comfortaa font-bold text-sm bg-lime text-ink hover:bg-lime-500 shadow-[0_4px_14px_-6px_rgba(188,239,60,0.7)]"
                  data-testid="navbar-signup-btn"
                >
                  {t('navStartNow')}
                </Button>
              </>
            )}
          </div>

          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-1">
            {user && <NotificationBell />}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className={transparent ? 'text-white' : 'text-ink-800'}
              aria-label="Idioma"
            >
              <Globe className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={transparent ? 'text-white' : 'text-ink-800'}
              data-testid="navbar-mobile-toggle"
              aria-label="Menú"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-black/5 bg-white">
          <div className="px-4 pt-3 pb-4 space-y-1 font-mont">
            {[
              { to: '/search', label: t('search') },
              { to: '/#mundial-2026', label: t('navWorldCup') },
              { to: '/how-it-works', label: t('howItWorks') },
              { to: '/become-mobilitier', label: t('becomeMobilitier') },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="block px-3 py-3 rounded-lg text-ink-800 hover:bg-ink-50 text-base"
                onClick={() => setMobileMenuOpen(false)}
              >
                {l.label}
              </Link>
            ))}

            {user ? (
              <>
                {user.role === 'admin' && (
                  <Link
                    to="/admin/dashboard"
                    className="block px-3 py-3 rounded-lg text-ink-800 hover:bg-ink-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin Panel
                  </Link>
                )}
                {user.role !== 'admin' && (user.role === 'user' || user.secondary_role === 'user') && (
                  <Link
                    to="/dashboard"
                    className="block px-3 py-3 rounded-lg text-ink-800 hover:bg-ink-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                )}
                {user.role !== 'admin' && (user.role === 'mobilitier' || user.secondary_role === 'mobilitier') && (
                  <Link
                    to="/mobilitier/dashboard"
                    className="block px-3 py-3 rounded-lg text-ink-800 hover:bg-ink-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Rental-Hub
                  </Link>
                )}
                <Link
                  to="/profile"
                  className="block px-3 py-3 rounded-lg text-ink-800 hover:bg-ink-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('myProfile')}
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-3 rounded-lg text-ink-800 hover:bg-ink-50"
                >
                  {t('logout')}
                </button>
              </>
            ) : (
              <div className="pt-2 grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="rounded-full h-11 border-ink-200 text-ink-800"
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                >
                  {t('login')}
                </Button>
                <Button
                  className="rounded-full h-11 bg-lime hover:bg-lime-500 text-ink font-comfortaa font-bold"
                  onClick={() => {
                    navigate('/signup');
                    setMobileMenuOpen(false);
                  }}
                >
                  {t('navStartNow')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
